import { CRITICALITY_LABELS } from "constants/reviewSummary";

import {
  CHANGE_ITEM_VARIANT,
  ITEM_ACTIVITY_ROUTE_BY_OWNER_TYPE,
  NO_SKIP_NOTE_MESSAGE,
} from "./constants";

export const buildItemActivityRoute = (
  ownerType,
  ownerId,
  isArchived = false
) => {
  const buildRoute = ITEM_ACTIVITY_ROUTE_BY_OWNER_TYPE[ownerType];
  return buildRoute ? buildRoute(ownerId, isArchived) : null;
};

export const criticalityDisplayLabel = (code) =>
  CRITICALITY_LABELS[code] ?? code;

const changeLocationLabel = (change) =>
  change.sheet
    ? `Paragraph ${change.paragraph} (${change.sheet})`
    : `Paragraph ${change.paragraph}`;

export const formatChangeEntry = (change) => {
  const location = changeLocationLabel(change);

  switch (change.type) {
    case "CRITICALITY":
      return `${location}: Review Status set to ${criticalityDisplayLabel(change.to)} from ${criticalityDisplayLabel(change.from)}`;
    case "NOTE_ADDED":
      return `${location}: Comment added — "${change.text}"`;
    case "NOTE_CHANGED":
      return `${location}: Comment changed to "${change.to}"`;
    case "NOTE_REMOVED":
      return `${location}: Comment removed`;
    default:
      return location;
  }
};

export const gateBlocksFrom = (metadata) => {
  const gates = metadata?.gates ?? [];
  return gates
    .filter((gate) => gate.outcome !== "UNGATED")
    .map((gate) => ({
      heading: gate.fromStage,
      transition: `${gate.fromStage} → ${gate.toStage}`,
      isSkipped: gate.outcome === "SKIPPED",
      skipNote: gate.skipNote ?? null,
      responses: gate.responses ?? [],
    }));
};

export const changeListItems = (changes) =>
  changes.map((change, index) => ({
    key: `${change.type}-${change.paragraph}-${index}`,
    variant: CHANGE_ITEM_VARIANT.bullet,
    text: formatChangeEntry(change),
  }));

export const gateListItems = (gateBlocks) =>
  gateBlocks.flatMap((gate) => {
    const headingItem = {
      key: `${gate.heading}-heading`,
      variant: CHANGE_ITEM_VARIANT.heading,
      text: gate.heading,
    };

    if (gate.isSkipped) {
      return [
        headingItem,
        {
          key: `${gate.heading}-skip`,
          variant: CHANGE_ITEM_VARIANT.note,
          text: gate.skipNote ?? NO_SKIP_NOTE_MESSAGE,
        },
      ];
    }

    return [
      headingItem,
      ...gate.responses.map((response, index) => ({
        key: `${gate.heading}-${index}`,
        variant: CHANGE_ITEM_VARIANT.qa,
        question: response.question,
        answer: response.answer,
      })),
    ];
  });

export const changeTruncatedCount = (metadata, changes) => {
  if (!metadata?.truncated) {
    return 0;
  }
  const totalCount = Math.max(
    metadata.changeCount ?? changes.length,
    changes.length
  );
  return totalCount - changes.length;
};

export const gateTruncatedCount = (metadata, gateBlocks) => {
  if (!metadata?.truncated) {
    return 0;
  }
  const renderedResponses = gateBlocks.reduce(
    (total, gate) => total + gate.responses.length,
    0
  );
  return Math.max(
    0,
    (metadata.responseCount ?? renderedResponses) - renderedResponses
  );
};

export const activityListPropsFrom = (metadata) => {
  const changes = metadata?.changes ?? [];

  if (changes.length > 0) {
    return {
      items: changeListItems(changes),
      label: "List of changes",
      truncatedCount: changeTruncatedCount(metadata, changes),
    };
  }

  const gateBlocks = gateBlocksFrom(metadata);
  if (gateBlocks.length > 0) {
    return {
      items: gateListItems(gateBlocks),
      truncatedCount: gateTruncatedCount(metadata, gateBlocks),
    };
  }

  return null;
};
